import _ from 'lodash'
import { useState, useEffect, useCallback, MouseEvent } from 'react'
import { connect } from 'react-redux'
import { reduxForm } from 'redux-form'
import { LoadingOutlined, QuestionCircleFilled } from '@ant-design/icons'
import { Modal } from 'antd'
import { DDP_HTS_OVERSEAS_ORDER_LIMIT } from '@Orders/constants'
import { HtsFormContext } from '@Orders/OrderDetailsPage/context'

import Box from '~/core/Box'
import styles from '../styles.css'
import { salesOrder_salesOrder_salesOrderLines_items as SalesOrderLineItemType } from '../Queries/__generated__/salesOrder'
import { DefaultRootState } from '~/types'

import HTSForm from './HTSForm'
import HTS5106Info from './HTS5106Info'
import HTSSubheader from './HTSSubheader'
import {
    downloadCsv,
    getHtsInfo,
    updateHtsMutation,
    getHtsFormValues,
    successMessageHtsSubmission,
    submissionFailedError,
    htsSubmitModalWidth,
    selectFormValues,
    formSyncErrors,
    TYPE_OF_HTS,
    ORDER_TYPE,
    FORM_CONFIG,
    PROTOTYPING_HTS_INITIAL,
    updateHtsInfo,
} from './HTSLogic'

interface HTSSectionPropsType {
    checked5106: boolean
    dispatch: Function
    errors: {}
    invalid: boolean
    isAllHtsValuesSubmitted: boolean
    isAnylineWithProcessName?: boolean
    isImOrder: boolean
    isLocatedInAdminPage?: boolean
    onCheckboxChange: Function
    orderId: string
    orderName: string
    pristine: boolean
    salesOrderLines: SalesOrderLineItemType[]
    selectedValues: {}
    submitting: boolean
    totalOverseasAmount: number
    totalOverseasAmountInDollars: number
    typeOfHts: string
    userId?: number
    isHTSSubmissionTimeExpired: boolean
}

const HTSSection = (props: HTSSectionPropsType) => {
    const {
        checked5106,
        dispatch,
        errors,
        invalid,
        isAllHtsValuesSubmitted,
        isAnylineWithProcessName,
        isImOrder,
        isLocatedInAdminPage,
        onCheckboxChange,
        orderId,
        orderName,
        pristine,
        salesOrderLines,
        selectedValues,
        submitting,
        totalOverseasAmountInDollars,
        typeOfHts,
        userId,
        isHTSSubmissionTimeExpired,
    } = props

    const [submitModal, setSubmitModal] = useState(false)
    const [htsSubmitting, setHtsSubmitting] = useState(false)

    const HtsFormContextValue = { isImOrder, isAnylineWithProcessName }

    useEffect(() => {
        if (
            isAllHtsValuesSubmitted &&
            isAllHtsValuesSubmitted === htsSubmitting
        ) {
            successMessageHtsSubmission()
            setSubmitModal(false)
            setHtsSubmitting(false)
        }
    }, [isAllHtsValuesSubmitted, htsSubmitting])

    const validationForHtsCode = useCallback(
        (value: string) => {
            const minLength = 12
            const valueToCheck = value && value.trim()

            if (
                valueToCheck &&
                valueToCheck.startsWith(PROTOTYPING_HTS_INITIAL, 0)
            ) {
                if (typeOfHts === TYPE_OF_HTS.COMMERCIAL) {
                    return 'This code is for prototyping parts only'
                }
                return 'Cannot be the same as primary HTS'
            }

            if (valueToCheck && valueToCheck.length < minLength) {
                return 'Incomplete entry'
            }
            return null
        },
        [typeOfHts],
    )

    const updateHtsSubmitting = () => {
        setSubmitModal(false)
        setHtsSubmitting(false)
    }

    const onPreSubmit = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault()
        if (orderId.indexOf(ORDER_TYPE.PLATFORM) > -1) {
            submissionFailedError()
        } else {
            setSubmitModal(true)
        }
    }

    const checkOrderQualifiesDdp = () =>
        typeOfHts === TYPE_OF_HTS.PROTOTYPING &&
        totalOverseasAmountInDollars > DDP_HTS_OVERSEAS_ORDER_LIMIT

    const saveAndSubmitLater = () => {
        if (orderId.indexOf(ORDER_TYPE.PLATFORM) > -1) {
            submissionFailedError()
            return null
        }
        const htsInfo =
            salesOrderLines &&
            selectedValues &&
            getHtsInfo({
                formData: selectedValues,
                isItReadyToBeSubmitted: false,
                typeOfHts,
                salesOrderLines,
            })

        if (HtsFormContextValue.isImOrder) {
            updateHtsInfo(htsInfo, salesOrderLines)
        }

        const isOrderQualifiesDdp = checkOrderQualifiesDdp()

        return dispatch(
            updateHtsMutation(
                orderId,
                htsInfo,
                orderName,
                false,
                isOrderQualifiesDdp,
                isLocatedInAdminPage && userId ? userId : null,
            ),
        )
    }

    const onDownloadCsv = (evt: Event) => {
        evt.preventDefault()
        downloadCsv(typeOfHts)
    }

    const submitDetails = () => {
        if (!dispatch) {
            return null
        }
        setHtsSubmitting(true)
        const htsInfo =
            salesOrderLines &&
            selectedValues &&
            getHtsInfo({
                formData: selectedValues,
                isItReadyToBeSubmitted: true,
                typeOfHts,
                salesOrderLines,
            })

        if (HtsFormContextValue.isImOrder) {
            updateHtsInfo(htsInfo, salesOrderLines)
        }

        const isOrderQualifiesDdp = checkOrderQualifiesDdp()

        return dispatch(
            updateHtsMutation(
                orderId,
                htsInfo,
                orderName,
                true,
                isOrderQualifiesDdp,
                isLocatedInAdminPage && userId ? userId : null,
                updateHtsSubmitting,
            ),
        )
    }

    if (!isHTSSubmissionTimeExpired && !typeOfHts) {
        return null
    }
    const isCommercial = typeOfHts === TYPE_OF_HTS.COMMERCIAL

    return (
        <>
            <HTSSubheader
                isAllHtsValuesSubmitted={isAllHtsValuesSubmitted}
                typeOfHts={typeOfHts}
                onDownloadCsv={onDownloadCsv}
            />
            <HtsFormContext.Provider value={HtsFormContextValue}>
                <Box direction="col">
                    <HTSForm
                        salesOrderLines={salesOrderLines}
                        typeOfHts={typeOfHts}
                        isAllHtsValuesSubmitted={isAllHtsValuesSubmitted}
                        selectedValues={selectedValues}
                        submitting={submitting}
                        invalid={invalid}
                        errors={errors}
                        pristine={pristine}
                        onPreSubmit={onPreSubmit}
                        saveAndSubmitLater={saveAndSubmitLater}
                        validationForHtsCode={validationForHtsCode}
                        isHTSSubmissionTimeExpired={isHTSSubmissionTimeExpired}
                    />
                </Box>
            </HtsFormContext.Provider>
            {isCommercial && (
                <HTS5106Info
                    checked5106={checked5106}
                    onCheckboxChange={onCheckboxChange}
                    isHTSSubmissionTimeExpired={isHTSSubmissionTimeExpired}
                />
            )}
            <Modal
                width={htsSubmitModalWidth}
                title={
                    <>
                        <QuestionCircleFilled className={styles.modalIcon} />
                        OK to submit?
                    </>
                }
                visible={submitModal}
                onOk={() => submitDetails()}
                onCancel={() => setSubmitModal(false)}
                okText={
                    htsSubmitting ? (
                        <>
                            <LoadingOutlined spin /> Submitting
                        </>
                    ) : (
                        'Yes, submit'
                    )
                }
                closable={false}
                className={styles.submitModal}
            >
                <p>
                    After submission, you will need to contact Fictiv in order
                    to make any edits.
                </p>
            </Modal>
        </>
    )
}

export default _.flowRight(
    connect((state: DefaultRootState, props: HTSSectionPropsType) => ({
        initialValues:
            props.salesOrderLines && getHtsFormValues(props.salesOrderLines),
        selectedValues: selectFormValues(state),
        errors: formSyncErrors(state),
    })),
    reduxForm(FORM_CONFIG),
)(HTSSection)
